import type { ChannelMember } from '@src/api/shared/community/channelMemberApiClient';
import { TENANT_ROLE } from '@src/enum/tenantRoleEnum';
import CustomImage from '@src/ui/Editor/extensions/customImage';
import CustomTextStyle from '@src/ui/Editor/extensions/customTextStyle';
import Iframe from '@src/ui/Editor/extensions/iframe';
import { MentionMark } from '@src/ui/Editor/extensions/mentionMark';
import { MentionSuggestion } from '@src/ui/Editor/extensions/mentionSuggestion';
import PasteFilter from '@src/ui/Editor/extensions/pasteFilter';
import TableCellWithColor from '@src/ui/Editor/extensions/tableCellWithColor';
import TableColwidthSync from '@src/ui/Editor/extensions/tableColwidthSync';
import CellDecorationExtension from '@src/ui/Editor/plugins/cellDecorationExtension';
import CellSelectionExtension from '@src/ui/Editor/plugins/cellSelectionExtension';
import Blockquote from '@tiptap/extension-blockquote';
import Heading from '@tiptap/extension-heading';
import Link from '@tiptap/extension-link';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Strike } from '@tiptap/extension-strike';
import Table from '@tiptap/extension-table';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useMemo, useRef } from 'react';

import type { EditorFeatures } from '../types';

interface UseTiptapExtensionsProps {
  enabled: EditorFeatures;
  enableMentions?: boolean;
  placeholderText?: string;
  channelMembers?: ChannelMember[];
  currentUserId?: number;
  currentUserType?: string;
  isLoggedIn?: boolean;
  enablePasteFiltering?: boolean;
  removeTenantMentions?: boolean;
}

export function useTiptapExtensions({
  enabled,
  enableMentions = false,
  placeholderText = '',
  channelMembers = [],
  currentUserId,
  currentUserType,
  isLoggedIn = true,
  enablePasteFiltering = false,
  removeTenantMentions = false,
}: UseTiptapExtensionsProps) {
  const channelMembersRef = useRef(channelMembers);

  useEffect(() => {
    channelMembersRef.current = channelMembers;
  }, [channelMembers]);

  const extensions = useMemo(() => {
    return [
      ...(enablePasteFiltering
        ? [
            PasteFilter.configure({
              allowTextFormatting: enabled.textStyles,
              allowLists: enabled.lists,
              allowLinks: enabled.links,
              allowBlockquotes: enabled.quotes,
              allowMentions: enableMentions,
              allowHeadings: false,
              allowTables: enabled.tables,
              removeTenantMentions: removeTenantMentions,
              getChannelMembers: () => channelMembersRef.current,
              excludedRoleCodes: [TENANT_ROLE.MEMBER],
            }),
          ]
        : []),

      // Configurable placeholder
      Placeholder.configure({
        placeholder: placeholderText,
        emptyEditorClass: 'is-editor-empty',
        showOnlyWhenEditable: false,
      }),

      // StarterKit with custom configuration
      StarterKit.configure({
        strike: false,
        heading: false,
        blockquote: false,
      }),

      // Heading with custom keyboard shortcuts
      Heading.extend({
        addKeyboardShortcuts() {
          return {
            'Mod-Alt-0': () =>
              enabled.shortcut.heading ? this.editor.commands.setParagraph() : true,
            'Mod-Alt-1': () =>
              enabled.shortcut.heading ? this.editor.commands.toggleHeading({ level: 1 }) : true,
            'Mod-Alt-2': () =>
              enabled.shortcut.heading ? this.editor.commands.toggleHeading({ level: 2 }) : true,
            'Mod-Alt-3': () =>
              enabled.shortcut.heading ? this.editor.commands.toggleHeading({ level: 3 }) : true,
          };
        },
      }).configure({
        levels: [1, 2, 3],
      }),

      // Strike with custom keyboard shortcut
      Strike.extend({
        addKeyboardShortcuts() {
          return {
            'Mod-Shift-x': () =>
              enabled.shortcut.strike ? this.editor.commands.toggleStrike() : true,
          };
        },
      }),

      // Blockquote with disabled keyboard shortcut
      Blockquote.extend({
        addKeyboardShortcuts() {
          return {
            'Mod-Shift-b': () =>
              enabled.shortcut.blockquote ? this.editor.commands.toggleBlockquote() : true,
          };
        },
      }),

      // Underline with conditional keyboard shortcut
      enabled.textStyles
        ? Underline.extend({
            addKeyboardShortcuts() {
              return {
                'Mod-u': () =>
                  enabled.shortcut.underline ? this.editor.commands.toggleUnderline() : true,
              };
            },
          })
        : null,

      enabled.textStyles ? CustomTextStyle : null,

      // Text align with dynamic types
      enabled.textStyles
        ? TextAlign.extend({
            addKeyboardShortcuts() {
              return {
                'Mod-Shift-l': () =>
                  enabled.shortcut.textAlign ? this.editor.commands.setTextAlign('left') : true,
                'Mod-Shift-e': () =>
                  enabled.shortcut.textAlign ? this.editor.commands.setTextAlign('center') : true,
                'Mod-Shift-r': () =>
                  enabled.shortcut.textAlign ? this.editor.commands.setTextAlign('right') : true,
              };
            },
          }).configure({
            types: [
              'paragraph',
              'heading',
              ...(enabled.images ? ['image'] : []),
              ...(enabled.tables ? ['tableCell', 'tableHeader'] : []),
              ...(enabled.iframes ? ['iframe'] : []),
            ],
            alignments: ['left', 'center', 'right'],
          })
        : null,

      // Image extension
      enabled.images ? CustomImage.configure({ inline: false }) : null,

      // Link extension with community-specific configuration
      enabled.links
        ? Link.extend({
            parseHTML() {
              return [{ tag: 'a[href]:not([data-mention-id])' }];
            },
            addInputRules() {
              return [];
            },
          }).configure({
            openOnClick: false,
            autolink: false,
            HTMLAttributes: {
              class: enableMentions ? 'link-community' : 'link-default',
            },
          })
        : null,

      // Iframe extension
      enabled.iframes ? Iframe : null,

      // Table extensions
      enabled.tables ? Table.configure({ resizable: true }) : null,
      enabled.tables ? TableCellWithColor : null,
      enabled.tables ? TableHeader : null,
      enabled.tables ? TableRow : null,
      enabled.tables ? CellDecorationExtension : null,
      enabled.tables ? CellSelectionExtension : null,
      enabled.tables ? TableColwidthSync : null,

      // Mention extensions (configurable)
      enableMentions
        ? MentionMark.configure({
            currentUserId,
            currentUserType,
          })
        : null,
      enableMentions && isLoggedIn
        ? MentionSuggestion.configure({ members: channelMembers })
        : null,
    ].filter((ext): ext is NonNullable<typeof ext> => ext !== null);
  }, [
    enabled,
    enableMentions,
    placeholderText,
    channelMembers,
    currentUserId,
    currentUserType,
    isLoggedIn,
    enablePasteFiltering,
    removeTenantMentions,
  ]);

  return extensions;
}
